import { useState, useEffect, useCallback, useRef } from 'react';
import { blocks, transactions, networkStats, tokenPrice } from '@/data/blockchainData';

export interface TronSocketData {
  // Network stats
  totalAccounts?: number;
  totalTransactions?: number;
  totalValueLocked?: number;
  totalTransferVolume?: number;
  currentTPS?: number;
  maxTPS?: number;
  totalNodes?: number;
  totalContracts?: number;
  totalTokens?: number;
  
  // 24h changes
  accountsChange24h?: number;
  transactionsChange24h?: number;
  tvlChange24h?: number;
  volumeChange24h?: number;
  
  // Token prices
  trxPrice?: number;
  trxPriceChange24h?: number;
  trxMarketCap?: number;
  trxVolume24h?: number;
  totalSupply?: number;
  totalStaked?: number;
  stakedChange?: number;
  
  // Latest blocks
  latestBlocks?: Array<{
    number: number;
    hash: string;
    timestamp: number;
    txCount: number;
    producer: string;
    producerName: string;
    reward: number;
    fee: number;
  }>;
  
  // Latest transactions
  latestTransactions?: Array<{
    hash: string;
    timestamp: number;
    from: string;
    to: string;
    amount: number;
    tokenName: string;
    confirmed: boolean;
    type: string;
  }>;
  
  // Connection status
  isConnected: boolean;
  lastUpdate?: Date;
}

// WebSocket URL - Tronscan homepage socket
const SOCKET_URL = 'wss://apilist.tronscan.org/api/tronsocket/homepage';

// Get mock data as fallback
const getMockData = (): Partial<TronSocketData> => ({
  totalAccounts: networkStats.totalAccounts,
  totalTransactions: networkStats.totalTransactions,
  totalValueLocked: networkStats.totalValueLocked,
  totalTransferVolume: networkStats.totalTransferVolume,
  currentTPS: networkStats.currentTPS,
  maxTPS: networkStats.maxTPS,
  totalNodes: networkStats.totalNodes,
  totalContracts: networkStats.totalContracts,
  totalTokens: networkStats.totalTokens,
  accountsChange24h: networkStats.accountsChange24h,
  transactionsChange24h: networkStats.transactionsChange24h,
  tvlChange24h: networkStats.tvlChange24h,
  volumeChange24h: networkStats.volumeChange24h,
  trxPrice: tokenPrice.price,
  trxPriceChange24h: tokenPrice.change24h,
  trxMarketCap: tokenPrice.marketCap,
  trxVolume24h: tokenPrice.volume24h,
  totalSupply: tokenPrice.totalSupply,
  totalStaked: tokenPrice.totalStaked,
  stakedChange: tokenPrice.stakedChange,
  latestBlocks: blocks.map(block => ({
    number: block.number,
    hash: block.hash,
    timestamp: block.timestamp instanceof Date ? block.timestamp.getTime() : block.timestamp,
    txCount: block.transactionCount,
    producer: block.validator,
    producerName: block.validatorName,
    reward: block.reward,
    fee: block.burned,
  })),
  latestTransactions: transactions.map(tx => ({
    hash: tx.hash,
    timestamp: tx.timestamp instanceof Date ? tx.timestamp.getTime() : tx.timestamp,
    from: tx.from,
    to: tx.to,
    amount: tx.amount,
    tokenName: tx.token,
    confirmed: true,
    type: tx.type,
  })),
});

export const useTronSocket = () => {
  const [data, setData] = useState<TronSocketData>({
    isConnected: false,
    ...getMockData(),
  });
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef(false);

  const parseWebSocketMessage = useCallback((message: any) => {
    setData(prev => {
      const updated: TronSocketData = {
        ...prev,
        lastUpdate: new Date(),
      };

      // Parse tron_info data
      const tronInfo = message.tron_info;
      if (tronInfo) {
        if (tronInfo.account_number) updated.totalAccounts = tronInfo.account_number;
        if (tronInfo.transaction_number) updated.totalTransactions = tronInfo.transaction_number;
        if (tronInfo.freeze_balance) updated.totalValueLocked = parseFloat(tronInfo.freeze_balance);
        if (tronInfo.vip_core_token_amount_his_total) updated.totalTransferVolume = tronInfo.vip_core_token_amount_his_total;
        
        if (tronInfo.data) {
          if (tronInfo.data.currentTps) updated.currentTPS = tronInfo.data.currentTps;
          if (tronInfo.data.maxTps) updated.maxTPS = tronInfo.data.maxTps;
          if (tronInfo.data.nodeNum) updated.totalNodes = tronInfo.data.nodeNum;
        }
        
        if (tronInfo.contract_number) updated.totalContracts = tronInfo.contract_number;
        if (tronInfo.token_number) updated.totalTokens = tronInfo.token_number;
        
        if (tronInfo.last_24h_account_change) updated.accountsChange24h = tronInfo.last_24h_account_change;
        if (tronInfo.last_24h_transaction_change) updated.transactionsChange24h = tronInfo.last_24h_transaction_change;
        if (tronInfo.last_24h_tvl_change !== undefined) updated.tvlChange24h = tronInfo.last_24h_tvl_change * 100;
        if (tronInfo.vip_core_token_1day_add) updated.volumeChange24h = tronInfo.vip_core_token_1day_add;
        
        // Total supply and staked
        if (tronInfo.total_supply) updated.totalSupply = tronInfo.total_supply;
        if (tronInfo.turnover) updated.totalSupply = tronInfo.turnover;
        if (tronInfo.freeze_balance) updated.totalStaked = parseFloat(tronInfo.freeze_balance);
      }

      // Parse latest_block_info
      if (message.latest_block_info?.data && Array.isArray(message.latest_block_info.data)) {
        updated.latestBlocks = message.latest_block_info.data.map((block: any) => ({
          number: block.number,
          hash: block.hash || '',
          timestamp: block.timestamp,
          txCount: block.nrOfTrx || 0,
          producer: block.witnessAddress || '',
          producerName: block.witnessName || '',
          reward: block.blockReward || 8,
          fee: block.fee || 0,
        }));
      }

      // Parse latest_transaction_info
      if (message.latest_transaction_info?.data && Array.isArray(message.latest_transaction_info.data)) {
        updated.latestTransactions = message.latest_transaction_info.data.map((tx: any) => {
          const toAddress = tx.toAddressList?.[0] || tx.toAddress || '';
          return {
            hash: tx.hash || '',
            timestamp: tx.timestamp,
            from: tx.ownerAddress || '',
            to: toAddress,
            amount: parseFloat(tx.amount) / 1e6 || 0,
            tokenName: tx.tokenId === '_' ? 'TRX' : (tx.tokenAbbr || 'TRX'),
            confirmed: tx.confirmed ?? true,
            type: tx.contractType === '1' || tx.contractType === 1 ? 'Transfer TRX' : 
                  tx.contractType === '31' || tx.contractType === 31 ? 'Trigger Contract' :
                  tx.contractType === '2' || tx.contractType === 2 ? 'Transfer Token' : 'Transfer',
          };
        });
      }

      return updated;
    });
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || isConnectingRef.current) {
      return;
    }

    isConnectingRef.current = true;

    try {
      const ws = new WebSocket(SOCKET_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        isConnectingRef.current = false;
        setData(prev => ({ ...prev, isConnected: true }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          parseWebSocketMessage(message);
        } catch {
          // Silently ignore parsing errors - mock data is already loaded
        }
      };

      ws.onerror = () => {
        // Silently handle errors - mock data is already loaded as fallback
        isConnectingRef.current = false;
        setData(prev => ({ ...prev, isConnected: false }));
      };

      ws.onclose = () => {
        isConnectingRef.current = false;
        setData(prev => ({ ...prev, isConnected: false }));
        
        // Reconnect after 10 seconds (longer interval to reduce noise)
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 10000);
      };
    } catch {
      // Silently fail - mock data is already loaded
      isConnectingRef.current = false;
      setData(prev => ({ ...prev, isConnected: false }));
    }
  }, [parseWebSocketMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    isConnectingRef.current = false;
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, isConnected: data.isConnected, reconnect: connect };
};
